import { useCallback, useEffect, useState } from 'react'
import {
  SAFETY_BRIEFING,
  EVENT_PLAN_FALLBACK,
  FIRST_AID
} from '../components/live/resourcesData'

// useResourceOverrides — localStorage-backed copy-tuning overrides for the
// Quick Reference panel's text-bearing tabs. Pairs with /resources's
// Resources Edit Defaults row and per-event click-to-edit cells.
//
// PURE FRONTEND. No backend writes; the panel reads overrides at render
// time and falls back to the existing data layer when no override is set.
// Lets Chris iterate on wording without a TS edit + redeploy cycle.
//
// Resolution order per (eventId, category):
//   1. per-event override (event-{id}:{category})
//   2. defaults override   (defaults:{category})
//   3. server value         (Event Plan only — dbo.Events columns)
//   4. hard-coded constant in resourcesData.ts
//
// Storage shape (single key, flat keyed by 'scope:category'):
//
//   {
//     'defaults:briefing':       SafetyBriefingOverride,
//     'defaults:plan':           EventPlanOverride,
//     'defaults:first-aid':      FirstAidOverride,
//     'event-7:briefing':        SafetyBriefingOverride,
//     ...
//   }
//
// Corrupt JSON → fall back to empty store; storage operations wrapped in
// try/catch so a quota-exceeded or disabled-storage browser doesn't
// crash the app. localStorage quota is ~5 MB; the override payload is at
// most a few KB even with overrides on every event.

export const STORAGE_KEY = 'resource-overrides-v1'

export type EditableCategorySlug = 'briefing' | 'plan' | 'first-aid'

export interface EmergencyNumberOverride { name: string; phone: string }
export interface DefibLocationOverride   { location: string; where: string }

export interface SafetyBriefingOverride {
  warning?: string
  responsibilities?: string[]
  numbers?: EmergencyNumberOverride[]
}
export interface EventPlanOverride {
  operatingPattern?: string
  todaysProgramme?: string
  visitorFlow?: string
}
export interface FirstAidOverride {
  emergencyCalloutBody?: string
  defibLocations?: DefibLocationOverride[]
  firstAidKits?: string
}

export type CategoryOverride =
  | SafetyBriefingOverride
  | EventPlanOverride
  | FirstAidOverride

export type OverridesStore = Record<string, CategoryOverride>

export type Scope = 'defaults' | { eventId: number }

// ---- resolved-content shapes (what the panel consumes) -------------------

export interface SafetyBriefingContent {
  intro: string
  callout: { title: string; body: string }
  responsibilities: string[]
  emergencyNumbers: Array<{ label: string; tel: string; display: string }>
}

export interface EventPlanFromDb {
  operatingPattern: string | null
  todaysProgramme: string | null
  visitorFlow: string | null
}
export interface EventPlanContent {
  operatingPattern: string
  todaysProgramme: string
  visitorFlow: string
  // true when no per-event DB value or override is set — the panel renders
  // a subdued "using NYMR defaults" hint above the cards.
  usingFallback: boolean
}

export interface FirstAidContent {
  callout: { title: string; body: string }
  defibLocations: Array<{ location: string; where: string }>
  firstAidKits: string
}

// ---- helpers --------------------------------------------------------------

function scopeKey(scope: Scope, category: EditableCategorySlug): string {
  if (scope === 'defaults') return `defaults:${category}`
  return `event-${scope.eventId}:${category}`
}

function readStore(): OverridesStore {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as OverridesStore
    }
    return {}
  } catch {
    return {}
  }
}

function writeStore(store: OverridesStore): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch {
    // Quota exceeded, private mode, etc. The in-memory state still
    // reflects the override for this session — silent failure is fine
    // for a preview-only feature.
  }
}

// Phone tel: value cleaned to digits (or '999' style short codes).
function digitsOnly(raw: string): string {
  return raw.replace(/[^0-9]/g, '')
}

// ---- hook -----------------------------------------------------------------

export function useResourceOverrides() {
  const [overrides, setOverrides] = useState<OverridesStore>(() => readStore())

  // Cross-tab sync — listen for storage events fired by other windows /
  // tabs editing the same key. Strict mode in dev runs effects twice; the
  // handler is idempotent so a double-mount is harmless.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key !== STORAGE_KEY) return
      setOverrides(readStore())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const setOverride = useCallback(
    (scope: Scope, category: EditableCategorySlug, value: CategoryOverride) => {
      const key = scopeKey(scope, category)
      setOverrides((prev) => {
        const next = { ...prev, [key]: value }
        writeStore(next)
        return next
      })
    },
    []
  )

  const clearOverride = useCallback(
    (scope: Scope, category: EditableCategorySlug) => {
      const key = scopeKey(scope, category)
      setOverrides((prev) => {
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        writeStore(next)
        return next
      })
    },
    []
  )

  // ---- resolvers (the public read API) ------------------------------------

  const resolveSafetyBriefing = useCallback(
    (eventId: number | null): SafetyBriefingContent => {
      const perEvent = eventId != null
        ? overrides[`event-${eventId}:briefing`] as SafetyBriefingOverride | undefined
        : undefined
      const defaults = overrides['defaults:briefing'] as SafetyBriefingOverride | undefined

      const warning =
        perEvent?.warning ?? defaults?.warning ?? SAFETY_BRIEFING.callout.body
      const responsibilities =
        perEvent?.responsibilities ?? defaults?.responsibilities
          ?? [...SAFETY_BRIEFING.responsibilities]
      const numbers =
        perEvent?.numbers ?? defaults?.numbers
          ?? SAFETY_BRIEFING.emergencyNumbers.map((n) => ({
            name: n.label, phone: n.display
          }))

      return {
        intro: SAFETY_BRIEFING.intro,
        callout: { title: SAFETY_BRIEFING.callout.title, body: warning },
        responsibilities,
        emergencyNumbers: numbers.map((n) => ({
          label: n.name,
          tel: digitsOnly(n.phone),
          display: n.phone
        }))
      }
    },
    [overrides]
  )

  const resolveEventPlan = useCallback(
    (eventId: number | null, dbValue: EventPlanFromDb | null): EventPlanContent => {
      const perEvent = eventId != null
        ? overrides[`event-${eventId}:plan`] as EventPlanOverride | undefined
        : undefined
      const defaults = overrides['defaults:plan'] as EventPlanOverride | undefined

      function pick(field: 'operatingPattern' | 'todaysProgramme' | 'visitorFlow') {
        const fromPerEvent = perEvent?.[field]
        if (fromPerEvent != null) return { value: fromPerEvent, source: 'override' as const }
        const fromDb = dbValue?.[field]
        if (fromDb != null) return { value: fromDb, source: 'db' as const }
        const fromDefaults = defaults?.[field]
        if (fromDefaults != null) return { value: fromDefaults, source: 'override' as const }
        return { value: EVENT_PLAN_FALLBACK[field], source: 'fallback' as const }
      }

      const op = pick('operatingPattern')
      const tp = pick('todaysProgramme')
      const vf = pick('visitorFlow')

      return {
        operatingPattern: op.value,
        todaysProgramme: tp.value,
        visitorFlow: vf.value,
        usingFallback:
          op.source === 'fallback'
          && tp.source === 'fallback'
          && vf.source === 'fallback'
      }
    },
    [overrides]
  )

  const resolveFirstAid = useCallback(
    (eventId: number | null): FirstAidContent => {
      const perEvent = eventId != null
        ? overrides[`event-${eventId}:first-aid`] as FirstAidOverride | undefined
        : undefined
      const defaults = overrides['defaults:first-aid'] as FirstAidOverride | undefined

      const calloutBody =
        perEvent?.emergencyCalloutBody ?? defaults?.emergencyCalloutBody
          ?? FIRST_AID.callout.body
      const defibLocations =
        perEvent?.defibLocations ?? defaults?.defibLocations
          ?? FIRST_AID.defibLocations.map((d) => ({ location: d.location, where: d.where }))
      const firstAidKits =
        perEvent?.firstAidKits ?? defaults?.firstAidKits ?? FIRST_AID.firstAidKits

      return {
        callout: { title: FIRST_AID.callout.title, body: calloutBody },
        defibLocations,
        firstAidKits
      }
    },
    [overrides]
  )

  // True if any override exists in the given scope (used by the directory
  // grid to badge defaults/event rows that have been tuned).
  const hasOverride = useCallback(
    (scope: Scope, category?: EditableCategorySlug): boolean => {
      const prefix = scope === 'defaults' ? 'defaults:' : `event-${scope.eventId}:`
      const needle = category ? prefix + category : prefix
      return Object.keys(overrides).some((k) => k.startsWith(needle))
    },
    [overrides]
  )

  return {
    overrides,
    setOverride,
    clearOverride,
    hasOverride,
    resolveSafetyBriefing,
    resolveEventPlan,
    resolveFirstAid
  }
}
